import isEmpty from 'lodash/isEmpty';
import mixpanel from 'mixpanel-browser';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { createUserWithIdp, sendGridEmail , verifyRecaptchaApi, saveNewsletter, mailerLiteSyncSignup } from '../util/api';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { ACCOUNT_TYPE } from '../util/constants';
import { createPolicyURLAction } from '../containers/EditListingPage/EditListingPage.duck';

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;
const LATEST_TOS_VERSION = '2.0';

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

export const RECAPTCHA_REQUEST='app/auth/RECAPTCHA_REQUEST';
export const RECAPTCHA_ERROR = 'app/auth/RECAPTCHA_ERROR';



// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,

  recaptchaError: null
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };


    case RECAPTCHA_REQUEST:
      return { ...state, signupInProgress: false, recaptchaError: null }

    case RECAPTCHA_ERROR:
      return { ...state, signupInProgress: false, recaptchaError: payload }

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

export const reCaptchaRequest = e => ({ type: RECAPTCHA_REQUEST })
export const reCaptchaError = e => ({ type: RECAPTCHA_ERROR, payload: e })


// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password, token = false) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(() => {
      dispatch(loginSuccess());
      mixpanel.track('Login', {
        email: username,
        loginDate: new Date().toString(),
        environment: (process.env && process.env.REACT_APP_ENV) || 'development',
      });
    })
    .then(() => dispatch(fetchCurrentUser(null, token)))
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => async (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  const { email, password, firstName, lastName, recaptcha, ...rest } = params;
  const captcha = recaptcha.current.getValue();
  dispatch(reCaptchaRequest())
  const res = await verifyRecaptchaApi({ captchaValue: captcha })

  if (res?.success) {
    const createUserParams = isEmpty(rest)
      ? { email, password, firstName, lastName }
      : { email, password, firstName, lastName, publicData: { ...rest } };


    createUserParams.publicData = {
      ...(createUserParams.publicData || {}),
      TermsAndConditionVersion: {
        ...((createUserParams.publicData && createUserParams.publicData.TermsAndConditionVersion) || {}),
        [LATEST_TOS_VERSION]: true,
      },
    };

    // We must login the user if signup succeeds since the API doesn't
    // do that automatically.
    return sdk.currentUser
      .create(createUserParams)
      .then(async (res) => {
        const currentUser = res.data.data;
        dispatch(signupSuccess());
        mixpanel.people.set_once({
          distinct_id: (currentUser.id && currentUser.id.uuid) || email,
          name: `${firstName} ${lastName}`,
          email: createUserParams.email,
          first_name: firstName,
          last_name: lastName
        });
        mixpanel.identify((currentUser.id && currentUser.id.uuid) || email);
        mixpanel.track('Account Created', {
          distinct_id: currentUser.id.uuid,
          user_id: currentUser.id.uuid,
          email: createUserParams.email,
          accountType: createUserParams.publicData.accountType,
          stepCompleted: createUserParams.publicData.stepCompleted,
          mainProfession: createUserParams.publicData.mainProfession,
          availableHoursPerWeek: createUserParams.publicData.availableHoursPerWeek,
          environment: (process.env && process.env.REACT_APP_ENV) || 'development',
          createdDate: new Date().toString()
        });
        // createPersonApi({
        //   user_id: currentUser.id.uuid,
        //   email: createUserParams.email,
        //   accountType: createUserParams.publicData.accountType,
        //   name: `${firstName} ${lastName}`,
        //   email: createUserParams.email,
        //   first_name: firstName,
        //   last_name: lastName,
        //   companyName: createUserParams.publicData.companyName
        // }).then((res) => {
        //   if (res?.data?.data?.id) {
        //     sdk.currentUser.updateProfile({
        //       publicData: {
        //         pipedriveUserId: res.data.data.id,
        //       }
        //     })
        //   }

        // })
        const { accountType } = createUserParams?.publicData;
        // if ([ACCOUNT_TYPE.fractionalPro, ACCOUNT_TYPE.company].includes(accountType)) {
        //   scheduleDay3SignupEmail({
        //     id: currentUser?.id.uuid,
        //     firstName,
        //     email: createUserParams?.email,
        //     userType: accountType
        //   });
        // }
        // mailForCreateListing({ id: currentUser?.id.uuid, firstName, email: createUserParams?.email });
        if (accountType === ACCOUNT_TYPE.company || accountType === ACCOUNT_TYPE.fractionalPro) {
          dispatch(createPolicyURLAction({
            userId: currentUser.id.uuid,
            userName: firstName,
            email: email,
            type: accountType,
          }))

          saveNewsletter({ email, userType: accountType, firstName, lastName })
          // await emailToFractional({ email, signup: true })

          // Sync subscriber to MailerLite (fire-and-forget — never blocks signup)
          mailerLiteSyncSignup({
            email,
            userType: accountType,
            firstName,
            lastName,
          }).catch(err => console.error('[MailerLite] signup sync failed:', err));
        }
      })
      .then(() => dispatch(login(email, password, true)))
      .catch(e => {
        recaptcha.current.reset();
        dispatch(signupError(storableError(e)));
        log.error(e, 'signup-failed', {
          email: params.email,
          firstName: params.firstName,
          lastName: params.lastName,
        });
      });
  } else {
    console.log("Invalid Captcha...");
    recaptcha.current.reset();
    dispatch(reCaptchaError(storableError(res)));
  }
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(res => {
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};

export const requestEarlyAccess = params => (dispatch, getState, sdk) => {
  return (sendGridEmail({
    to: params,
    dynamic_template_data: {
      requestEmail: params
    },
    type: "EARLY_ACCESS_REQUEST"
  }))

};
